import { useViewTransition } from "./useViewTransition";
import { updateDoc, doc, getDoc } from "firebase/firestore";
import { firestore } from "@/helpers/firebase";
import { useLocation } from "react-router-dom";
import { useCallback } from "react";
import { SIGNED_IN_ROUTES } from "@skillscratch/shared-types";
import type { Page } from "@skillscratch/shared-types";

interface SignedInNavigation {
    setSignedInNavigationPage: (page: Page, userId: string) => Promise<void>;
    navigateToSignedInPage: (page: Page) => void;
}

export function useSignedInNavigation(): SignedInNavigation {
    const { navigateNormal } = useViewTransition(); 
    const { pathname } = useLocation();

    async function setSignedInNavigationPage(page: Page, userId: string) {
        // eslint-disable-next-line no-useless-catch
        try {
            const userDoc = await getDoc(doc(firestore, "user", userId));
            const currentPage = userDoc.data()?.newLoggedInNavigationPage;
            
            if (currentPage === SIGNED_IN_ROUTES.INDEX) {
                return;
            }
            
            await updateDoc(doc(firestore, "user", userId), {
                newLoggedInNavigationPage: page,
            });
        } catch (error) {
            // should probably show an error/handle with sentry/posthog
            // biome-ignore lint/complexity/noUselessCatch: <explanation>
            throw error;
        }
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const navigateToSignedInPage = useCallback(function navigateToSignedInPage(page: Page) {
        if (page === "/" && pathname === "/") {
            return;
        }
        
        // Not sure if I need to check with leading /, but seems safest
        if (page !== "/" && (pathname === `/${page}` || pathname === page)) {
            return;
        }
      
        const pageWithSlash = page.startsWith('/') ? page : `/${page}`;

        navigateNormal(pageWithSlash, {
          replace: false,
          viewTransition: true,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    return {
        setSignedInNavigationPage,
        navigateToSignedInPage,
    };
}